import React, { useMemo, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { ReservaEstacionamientoDisplay } from "../../../types/ReservaEstacionamiento";
import { formatCurrency } from "../../shared/utils/formatters";
import { useReservationTooltip } from "../contexts/TooltipContext";
import {
  getReservationBlockVisualStyle,
  RESERVATION_CONTAINER_STYLES,
  RESERVATION_TEXT_STYLES,
} from "../styles/reservationStyles";

interface EstacionamientoBlockProps {
  reserva: ReservaEstacionamientoDisplay;
  startsOutsideMonth: boolean;
  endsOutsideMonth: boolean;
  onClick?: (reserva: ReservaEstacionamientoDisplay) => void;
}

const EstacionamientoBlock: React.FC<EstacionamientoBlockProps> = ({
  reserva,
  startsOutsideMonth,
  endsOutsideMonth,
  onClick,
}) => {
  const theme = useTheme();
  const { showTooltip, hideTooltip } = useReservationTooltip();

  const zonaColors = theme.palette.zonas.estacionamiento;

  const textColor = theme.palette.getContrastText(zonaColors.border);

  const baseStyle = useMemo(() => {
    return getReservationBlockVisualStyle(
      startsOutsideMonth,
      endsOutsideMonth,
      zonaColors.border
    );
  }, [startsOutsideMonth, endsOutsideMonth, zonaColors.border]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClick?.(reserva);
    },
    [onClick, reserva]
  );
  
  const handleMouseEnter = useCallback(
    (event: React.MouseEvent) => {
      const rect = event.currentTarget.getBoundingClientRect();
      showTooltip(reserva, {
        x: rect.left + rect.width / 2,
        y: rect.top,
      });
    },
    [reserva, showTooltip]
  );

  const handleMouseLeave = useCallback(() => {
    hideTooltip();
  }, [hideTooltip]);
  
  return (
    <Box
      sx={{
        ...baseStyle,
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        position: "relative",
        transition: "box-shadow 180ms ease, opacity 180ms ease",
        ...(startsOutsideMonth
          ? {}
          : { borderLeft: `3px solid ${zonaColors.border}` }),
      }}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
        {startsOutsideMonth && (
          <Box sx={{ position: "absolute", left: 2, top: "50%", transform: "translateY(-50%)", color: textColor, opacity: 0.7 }}>
            <ArrowBackIosIcon sx={{ fontSize: 16 }} />
          </Box>
        )}
        <Box sx={{ ...RESERVATION_CONTAINER_STYLES, color: textColor }}>
          <Typography sx={RESERVATION_TEXT_STYLES.NOMBRE}>
            {reserva.nombre} {reserva.apellido}
          </Typography>
          <Typography sx={RESERVATION_TEXT_STYLES.PRECIO}>
            {formatCurrency(reserva.valor_total)}
          </Typography>
        </Box>
        {endsOutsideMonth && (
          <Box sx={{ position: "absolute", right: 2, top: "50%", transform: "translateY(-50%)", color: textColor, opacity: 0.7 }}>
            <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
          </Box>
        )}
    </Box>
  );
};

export default React.memo(EstacionamientoBlock);
